/**
 * UnreadMentionsBadge — polls /api/chat/mentions/unread every 30 s and shows
 * a count badge next to the Chat nav item when the current user has unread @mentions.
 */
import { useEffect, useState, useRef } from 'react'
import { api } from '../../api/client'

const POLL_MS = 30_000

interface UnreadMentionsBadgeProps {
  /** Optional: notify parent of the current count (e.g. to drive a nav badge). */
  onCountChange?: (count: number) => void
}

export function UnreadMentionsBadge({ onCountChange }: UnreadMentionsBadgeProps) {
  const [count, setCount] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  async function poll() {
    try {
      const result = await api.listUnreadMentions()
      const n = result.count ?? 0
      setCount(n)
      onCountChange?.(n)
    } catch {
      // swallow — badge is best-effort
    }
  }

  useEffect(() => {
    void poll()
    timerRef.current = setInterval(() => { void poll() }, POLL_MS)
    return () => {
      if (timerRef.current !== null) clearInterval(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (count === 0) return null

  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-semibold leading-none"
      title={`${count} unread mention${count === 1 ? '' : 's'}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}
